import { useCallback, useMemo } from 'react';
import * as Yup from 'yup';
import { notify } from '@app/ui';
import { useChangePasswordMutation } from '@app/main/api';
import { Form } from '@webinex/antik';
import { useTranslation } from 'react-i18next';

type FormValue = Yup.InferType<ReturnType<typeof useSchema>>;

function useSchema() {
  const { t } = useTranslation(undefined, { keyPrefix: 'users.passwordChange' });

  return useMemo(
    () =>
      Yup.object({
        current: Yup.string().nullable().required(),
        new: Yup.string()
          .password()
          .nullable()
          .required()
          .notOneOf([Yup.ref('current'), null], t('passwordCurrentMatchError')),
        newConfirmation: Yup.string()
          .nullable()
          .oneOf([Yup.ref('new'), null], t('passwordConfirmationMatchError'))
          .required(),
      }),
    [t],
  );
}

const useSubmit = () => {
  const [changePassword] = useChangePasswordMutation();
  const form = Form.useRef('password-change');

  return useCallback(
    async (value: FormValue) =>
      await changePassword(value)
        .unwrap()
        .then(() => notify.success.t('users.passwordChange.success'))
        .then(() => form.current!.resetForm()),
    [changePassword, form],
  );
};

const INITIAL_VALUE: FormValue = {
  current: '',
  new: '',
  newConfirmation: '',
};

export function ChangePasswordPanel() {
  const onSubmit = useSubmit();
  const schema = useSchema();

  return (
    <Form
      type="formik"
      uid="password-change"
      ns="users.passwordChange"
      initialValues={INITIAL_VALUE}
      validationSchema={schema}
      onSubmit={onSubmit}
    >
      <Form.Item name="current" required>
        <Form.AutoFocus />
        <Form.Password autoFocus />
      </Form.Item>
      <Form.Item name="new" required>
        <Form.Password />
      </Form.Item>
      <Form.Item name="newConfirmation" required>
        <Form.Password />
      </Form.Item>
    </Form>
  );
}
